import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Subscription as RxSubscription } from 'rxjs';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { GenerateUserPassword } from 'src/modules/user/components/generate-user-password/component';
import { ResetUserPassword } from 'src/modules/user/components/reset-user-password/component';
import { UserApiService } from 'src/modules/user/services/user-api.service';
import { UserDataSource } from 'src/modules/user/services/user-data-source.service';
import { UserExportService } from 'src/modules/user/services/user-export-service';
import { User } from '../../../../user/types/user';
import { DataCleanse } from '../data-cleanse/component';
import { DeleteAccountConfirm } from '../delete-account-confirm/component';

@Component({
  selector: 'user-admin-dashboard',
  templateUrl: 'component.html',
  styleUrls: ['component.scss'],
  standalone: false,
})
export class UsersDashboard implements OnInit, OnDestroy {
  @Input() currentTab: BehaviorSubject<string>;

  @ViewChild('typeFilter', { static: false })
  public typeFilter: MatButtonToggleGroup;
  public readonly defaultType = 'all';

  public DataSource = new UserDataSource(this.UserApi, 'user-admin', 'users');
  private Subscriptions: RxSubscription[] = [];

  public toggles = [
    { label: 'Working Partner', value: 'working-partner', icon: 'person' },
    {
      label: 'Retired Partner',
      value: 'retired-partner',
      icon: 'person_outline',
    },
    { label: 'Leaver Benefits', value: 'leaver-benefits', icon: 'elderly' },
    { label: 'Benefits in Redundancy ', value: 'benefits', icon: 'person_off' },
    { label: 'Inherited', value: 'inherited', icon: 'list' },
    { label: 'Deactivated', value: 'deactivated', icon: 'list' },
    { label: 'All', value: 'all', icon: 'list' },
  ];

  public loading: boolean;
  public type = 'all';
  public Users: User[];
  public PageEvent: PageEvent;
  public filter: {};

  public displayedColumns = [
    'status',
    'first_name',
    'last_name',
    'email',
    'employee_number',
    'branch_number',
    'actions',
  ];
  public exporting: 'users' | 'clubs' | 'bookings';

  constructor(
    private dialog: MatDialog,
    private UserApi: UserApiService,
    private UserExport: UserExportService,
  ) {}

  ngOnInit() {
    this.Subscriptions.push(
      this.DataSource.loading.subscribe(
        (_loading) => (this.loading = _loading),
      ),
    );
    this.Subscriptions.push(
      this.DataSource.connect().subscribe((_Users) => (this.Users = _Users)),
    );
    this.Subscriptions.push(
      this.DataSource.PageEvent.subscribe(
        (_PageEvent) => (this.PageEvent = _PageEvent),
      ),
    );
    this.Subscriptions.push(
      this.DataSource.filter.subscribe(
        (filter) => filter && this.DataSource.loadWithPageEvent(this.PageEvent),
      ),
    );
    this.Subscriptions.push(
      this.currentTab.subscribe(
        (tab) => 'user-admin' === tab && this.setType(this.defaultType),
      ),
    );
  }

  public setSearch(search: string) {
    this.DataSource.setFilter('searchString', search);
  }

  public setType(type: string) {
    this.DataSource.setFilter('type', type);
  }

  public exportUsers() {
    this.exporting = 'users';
    this.DataSource.export()
      .then((users) => {
        const [headerRow] = users;
        const headers = Object.keys(headerRow);
        const filename = 'users-export';

        CsvExportService.export(users, filename, {
          showLabels: true,
          headers,
        });
        this.exporting = null;
      })
      .catch(() => (this.exporting = null));
  }

  public clubsExport() {
    this.exporting = 'clubs';
    return this.UserExport.clubs('user-admin')
      .then((clubs) => {
        return Promise.all(
          clubs.map((club) => {
            return this.UserExport.clubsUserList(club.id, 'user-admin').then(
              (partnerList) => {
                const headers = ['employee_number'];
                const filename = `${club.title}-partner-export`;
                CsvExportService.export(partnerList, filename, {
                  showLabels: true,
                  headers,
                });
              },
            );
          }),
        );
      })
      .finally(() => (this.exporting = null));
  }

  public dataCleanse() {
    this.dialog.open(DataCleanse);
  }

  public resetUserPassword(User: User) {
    const config = new MatDialogConfig();

    config.disableClose = true;
    config.data = {
      User,
      dataSource: this.DataSource,
    };

    this.handleDialog(ResetUserPassword, config);
  }

  public generateUserPassword(User: User) {
    const config = new MatDialogConfig();

    config.disableClose = true;
    config.data = {
      User,
      dataSource: this.DataSource,
    };

    this.handleDialog(GenerateUserPassword, config);
  }

  private handleDialog(component, config) {
    this.Subscriptions.push(
      this.dialog
        .open(component, config)
        .afterClosed()
        .subscribe(() => {
          this.DataSource.loadWithPageEvent(this.PageEvent);
        }),
    );
  }

  public deleteUser(User: User) {
    this.dialog
      .open(DeleteAccountConfirm, {
        disableClose: true,
        data: User,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.DataSource.loadWithPageEvent(this.PageEvent);
        }
      });
  }

  ngOnDestroy() {
    this.DataSource.disconnect();
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }
}
